import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme } from '../contexts/ThemeContext';
import { useHints } from '../contexts/HintsContext';
import { useAuth } from '../contexts/AuthContext';
import { Btn } from '../components/Input';
import { IconMoon, IconSun, IconEye, IconEyeOff, IconCheck, IconAlert, IconUser, IconBuilding, IconLocation, IconLock, IconSettings, IconMoney, IconLink } from '../components/Icons';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import Modal from '../components/Modal';

// ─── Field helper ────────────────────────────────────────────────────────────

function Field({ label, hint, optional, children }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
        {label}
        {optional && <span style={{ marginLeft: '6px', textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--ink3)' }}>· {t('dashboardV2.fieldOptional')}</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0 }}>{hint}</p>}
    </div>
  );
}

function PwField({ label, value, onChange, show, onToggle, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          className="ds-input"
          value={value}
          onChange={onChange}
          style={{ paddingRight: '36px' }}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--ink3)', cursor: 'pointer', padding: '4px' }}>
          {show ? <IconEyeOff className="w-3.5 h-3.5" /> : <IconEye className="w-3.5 h-3.5" />}
        </button>
      </div>
      {hint && <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0 }}>{hint}</p>}
    </div>
  );
}

// ─── Password strength meter ─────────────────────────────────────────────────

function passwordStrength(pw, t) {
  if (!pw) return { score: 0, label: '', color: 'var(--ink3)' };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^a-zA-Z0-9]/.test(pw)) score += 1;
  if (score <= 1) return { score: 1, label: t('dashboardV2.passwordWeak'),     color: 'var(--err2)' };
  if (score === 2) return { score: 2, label: t('dashboardV2.passwordMedium'),  color: 'var(--acc2)' };
  if (score === 3) return { score: 3, label: t('dashboardV2.passwordGood'),    color: 'var(--ok2)' };
  return { score: 4, label: t('dashboardV2.passwordVeryGood'), color: 'var(--ok2)' };
}

// ─── Stat KPI box (mini) ─────────────────────────────────────────────────────

function MiniStat({ icon, label, value, color }) {
  return (
    <div className="ds-card p-4 flex items-center gap-3">
      <div style={{
        width: '36px', height: '36px', borderRadius: '8px',
        background: 'rgba(184,84,40,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: color || 'var(--acc2)', flexShrink: 0,
      }}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{label}</p>
        <p style={{ fontSize: '16px', fontWeight: 800, fontFamily: 'monospace', color: 'var(--ink)', marginTop: '2px' }}>{value}</p>
      </div>
    </div>
  );
}

// ─── Sekcja kart ─────────────────────────────────────────────────────────────

function Section({ icon, title, hint, children, accent }) {
  return (
    <div className="ds-card" style={{ padding: '20px 24px', ...(accent ? { borderLeft: '3px solid var(--acc)' } : {}) }}>
      <div className="flex items-start gap-3 mb-4">
        {icon && <div style={{ color: 'var(--acc2)', flexShrink: 0, marginTop: '2px' }}>{icon}</div>}
        <div className="flex-1 min-w-0">
          <h2 style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>{title}</h2>
          {hint && <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '2px 0 0' }}>{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Modal: Zmień email ──────────────────────────────────────────────────────

function ChangeEmailModal({ currentEmail, onClose, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!newEmail || newEmail === currentEmail) return;
    setSaving(true);
    try {
      await api.changeEmail(newEmail);
      toast.success(t('dashboardV2.emailChangeSent'));
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={t('dashboardV2.changeEmailModalTitle')} onClose={onClose} size="sm">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p style={{ fontSize: '11px', color: 'var(--ink3)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
          {t('dashboardV2.settingsEmailChangeIntro', { email: currentEmail })}
        </p>
        <Field label={t('dashboardV2.changeEmailNewLabel')}>
          <input
            type="email"
            className="ds-input"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="nowy@email.com"
            autoFocus
            required
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn type="submit" disabled={saving || !newEmail}>{saving ? '...' : t('dashboardV2.changeEmailButton')}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ─── Sekcja: Abonament Matterport (przeniesiona z klasycznego Dashboardu) ────

function MatterportSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const [tryb, setTryb] = useState('miesieczny');
  const [mies, setMies] = useState('');
  const [roczny, setRoczny] = useState({ kwota: '', data_od: '', liczba_miesiecy: '12' });
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const rok = now.getFullYear();
  const miesiac = now.getMonth() + 1;

  const load = useCallback(() => {
    api.getKoszty().then(rows => {
      const cur = rows.find(r => r.rok === rok && r.miesiac === miesiac);
      setMies(String(cur?.kwota || ''));
    }).catch(() => {});
    api.getKosztyMPRoczny().then(rows => {
      const r = rows.find(r => r.rok === rok);
      if (r) {
        setRoczny({
          kwota: String(r.kwota),
          data_od: r.data_od || '',
          liczba_miesiecy: String(r.liczba_miesiecy || 12),
        });
        setTryb('roczny');
      }
    }).catch(() => {});
  }, [rok, miesiac]);

  useEffect(() => { load(); }, [load]);

  const saveMies = async () => {
    setSaving(true);
    try {
      await api.saveKoszt({ rok, miesiac, kwota: Number(mies) || 0 });
      toast.success(t('toast.saved'));
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const saveRoczny = async () => {
    setSaving(true);
    try {
      await api.saveKosztMPRoczny({
        rok,
        kwota: Number(roczny.kwota) || 0,
        data_od: roczny.data_od || null,
        liczba_miesiecy: Number(roczny.liczba_miesiecy) || 12,
      });
      toast.success(t('toast.saved'));
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const rocznaRata = roczny.kwota
    ? Number(roczny.kwota) / (Number(roczny.liczba_miesiecy) || 12)
    : 0;

  return (
    <Section
      icon={<IconMoney className="w-4 h-4" />}
      title={t('dashboardV2.matterportTitle')}
      hint={t('dashboardV2.matterportHint')}
    >
      <div className="flex rounded-lg overflow-hidden mb-4" style={{ border: '1px solid var(--border)', width: 'fit-content' }}>
        {[
          { key: 'miesieczny', label: t('dashboardV2.matterportMonthly') },
          { key: 'roczny', label: t('dashboardV2.matterportYearly') },
        ].map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setTryb(opt.key)}
            className="px-4 py-1.5 transition-colors"
            style={tryb === opt.key
              ? { background: 'var(--acc)', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', border: 'none' }
              : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', border: 'none' }}>
            {opt.label}
          </button>
        ))}
      </div>

      {tryb === 'miesieczny' ? (
        <div className="flex flex-col gap-3">
          <Field label={t('dashboardV2.matterportCurrentMonth', { period: `${rok}-${String(miesiac).padStart(2, '0')}` })}>
            <input
              type="number" min="0" step="0.01" placeholder="0.00"
              className="ds-input"
              value={mies}
              onChange={e => setMies(e.target.value)}
            />
          </Field>
          <div>
            <Btn onClick={saveMies} disabled={saving}>{saving ? '...' : t('dashboardV2.matterportSaveMonthly')}</Btn>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="form-row-2">
            <Field label={t('dashboardV2.matterportYearlyAmount')}>
              <input
                type="number" min="0" step="0.01" placeholder="0.00"
                className="ds-input"
                value={roczny.kwota}
                onChange={e => setRoczny(r => ({ ...r, kwota: e.target.value }))}
              />
            </Field>
            <Field label={t('dashboardV2.matterportMonthsCount')}>
              <input
                type="number" min="1" max="24" step="1" placeholder="12"
                className="ds-input"
                value={roczny.liczba_miesiecy}
                onChange={e => setRoczny(r => ({ ...r, liczba_miesiecy: e.target.value }))}
              />
            </Field>
          </div>
          <Field label={t('dashboardV2.matterportStartDate')}>
            <input
              type="date"
              className="ds-input"
              value={roczny.data_od}
              onChange={e => setRoczny(r => ({ ...r, data_od: e.target.value }))}
            />
          </Field>
          {rocznaRata > 0 && (
            <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>
              {t('dashboardV2.matterportEstMonthly', { amount: `${rocznaRata.toFixed(2)} zł`, n: roczny.liczba_miesiecy })}
            </p>
          )}
          <div>
            <Btn onClick={saveRoczny} disabled={saving}>{saving ? '...' : t('dashboardV2.matterportSaveYearly')}</Btn>
          </div>
        </div>
      )}
    </Section>
  );
}

// ─── Główny UstawieniaV2 ──────────────────────────────────────────────────────

export default function UstawieniaV2() {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { enabled: hintsEnabled, setEnabled: setHintsEnabled } = useHints();
  const { logout } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  const [profile, setProfile] = useState({
    imie: '', firma: '', telefon: '', adres: '', nip: '',
    kraj: 'PL', waluta: 'PLN', konto_bankowe: '', strona_www: '',
    chce_fakture: false, chce_rachunek: false,
    klient_slug: '',
  });
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePlan, setProfilePlan] = useState('trial');
  const [profileCreatedAt, setProfileCreatedAt] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileInitial, setProfileInitial] = useState(null);

  const [pw, setPw] = useState({ stare: '', nowe: '', potwierdz: '' });
  const [showPw, setShowPw] = useState({ stare: false, nowe: false, potwierdz: false });
  const [pwSaving, setPwSaving] = useState(false);

  const [emailModal, setEmailModal] = useState(false);

  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.getSettings().then(data => {
      const initial = {
        imie: data.imie || '', firma: data.firma || '', telefon: data.telefon || '',
        adres: data.adres || '', nip: data.nip || '',
        kraj: data.kraj || 'PL', waluta: data.waluta || 'PLN',
        konto_bankowe: data.konto_bankowe || '', strona_www: data.strona_www || '',
        chce_fakture: !!data.chce_fakture, chce_rachunek: !!data.chce_rachunek,
        klient_slug: data.klient_slug || '',
      };
      setProfile(initial);
      setProfileInitial(initial);
      setProfileEmail(data.email || '');
      setProfilePlan(data.plan || 'trial');
      setProfileCreatedAt(data.created_at || '');
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));

    api.getDashboard().then(d => {
      const aktywne = (d.spacery || []).filter(s => s.status === 'aktywny');
      setStats({
        spaceryAktywne: d.liczba_aktywnych_spacerow ?? aktywne.length,
        narosla: (d.do_rozliczenia || []).reduce((s, x) => s + (x.kwota_narosla || 0), 0),
        niezaplacone: (d.faktury_niezaplacone || []).length + (d.pakiety_niezaplacone || []).length,
        przychod: d.przychod || 0,
      });
    }).catch(() => {});
  }, []);

  const set = (field, val) => setProfile(p => ({ ...p, [field]: val }));

  const isDirty = useMemo(() => {
    if (!profileInitial) return false;
    return JSON.stringify(profile) !== JSON.stringify(profileInitial);
  }, [profile, profileInitial]);

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.saveSettings(profile);
      toast.success(t('settings.saved'));
      setProfileInitial(profile);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const savePassword = async () => {
    if (!pw.stare || !pw.nowe || !pw.potwierdz) {
      toast.error(t('dashboardV2.validationFillAllPasswords')); return;
    }
    if (pw.nowe !== pw.potwierdz) {
      toast.error(t('auth.passwordMismatch')); return;
    }
    if (pw.nowe.length < 6) {
      toast.error(t('auth.passwordTooShort')); return;
    }
    setPwSaving(true);
    try {
      const r = await api.changePassword(pw.stare, pw.nowe);
      if (r?.token) localStorage.setItem('sp_token', r.token);
      toast.success(t('settings.passwordChanged'));
      setPw({ stare: '', nowe: '', potwierdz: '' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPwSaving(false);
    }
  };

  const toggleLang = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('sp_lang', lang);
    // Zapamiętaj wybór na koncie — trzyma się między urządzeniami/przeglądarkami.
    api.setLang(lang).catch(() => {});
  };

  const handleLogoutEverywhere = async () => {
    const ok = await confirm({
      title: t('dashboardV2.settingsLogoutConfirmTitle'),
      message: t('dashboardV2.settingsLogoutConfirmMessage'),
      confirmLabel: t('dashboardV2.settingsLogoutConfirmButton'),
      cancelLabel: t('common.cancel'),
    });
    if (!ok) return;
    logout();
  };

  const memberSince = profileCreatedAt
    ? new Date(profileCreatedAt).toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-GB', { year: 'numeric', month: 'long' })
    : '—';

  // Trial countdown — zakładamy 30 dni od created_at
  const trialDaysLeft = (() => {
    if (profilePlan !== 'trial' || !profileCreatedAt) return null;
    const created = new Date(profileCreatedAt);
    const expires = new Date(created);
    expires.setDate(expires.getDate() + 30);
    const days = Math.max(0, Math.ceil((expires - new Date()) / 86400000));
    return days;
  })();

  const pwStrength = passwordStrength(pw.nowe, t);

  return (
    <div>
      {emailModal && (
        <ChangeEmailModal
          currentEmail={profileEmail}
          onClose={() => setEmailModal(false)}
          onDone={() => {/* email change requires verify, no immediate update */}}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>{t('settings.title')}</h1>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>{profileEmail}</p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-3">
            <span style={{ fontSize: '11px', color: 'var(--acc2)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <IconAlert className="w-3.5 h-3.5" />
              {t('dashboardV2.settingsUnsavedChanges')}
            </span>
            <Btn onClick={saveProfile} disabled={profileSaving}>
              {profileSaving ? '...' : t('dashboardV2.settingsSaveProfile')}
            </Btn>
          </div>
        )}
      </div>

      {/* Stat zone */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <MiniStat
            icon={<IconBuilding className="w-5 h-5" />}
            label={t('dashboardV2.settingsKpiActiveSpaces')}
            value={String(stats.spaceryAktywne)}
          />
          <MiniStat
            icon={<IconAlert className="w-5 h-5" />}
            label={t('dashboardV2.settingsKpiAccruedDebts')}
            value={Math.round(stats.narosla).toLocaleString('pl-PL') + ' zł'}
            color={stats.narosla > 0 ? 'var(--err2)' : 'var(--ok2)'}
          />
          <MiniStat
            icon={<IconCheck className="w-5 h-5" />}
            label={t('dashboardV2.settingsKpiUnpaidInvoices')}
            value={String(stats.niezaplacone)}
            color={stats.niezaplacone > 0 ? 'var(--acc2)' : 'var(--ok2)'}
          />
          <MiniStat
            icon={<IconUser className="w-5 h-5" />}
            label={t('dashboardV2.settingsKpiClientSince')}
            value={memberSince === '—' ? '—' : memberSince.slice(0, 8)}
          />
        </div>
      )}

      {/* 2-kolumnowy layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* ── LEWA KOLUMNA: Profil pogrupowany ────────────────────── */}
        <div className="flex flex-col gap-4">

          {/* Dane osobowe */}
          <Section icon={<IconUser className="w-4 h-4" />} title={t('dashboardV2.settingsSectionPersonal')}>
            <div className="flex flex-col gap-3">
              <Field label={t('settings.fullName')}>
                <input className="ds-input" value={profile.imie} onChange={e => set('imie', e.target.value)} placeholder="Jan Kowalski" />
              </Field>
              <Field label={t('settings.phone')} optional>
                <input className="ds-input" value={profile.telefon} onChange={e => set('telefon', e.target.value)} placeholder="+48 500 000 000" />
              </Field>
              <Field label={t('settings.address')} optional>
                <input className="ds-input" value={profile.adres} onChange={e => set('adres', e.target.value)} placeholder="ul. Przykładowa 1, 00-000 Warszawa" />
              </Field>
            </div>
          </Section>

          {/* Firma */}
          <Section icon={<IconBuilding className="w-4 h-4" />} title={t('dashboardV2.settingsSectionCompany')} hint={t('dashboardV2.settingsCompanyHint')}>
            <div className="flex flex-col gap-3">
              <Field label={t('settings.company')} optional>
                <input className="ds-input" value={profile.firma} onChange={e => set('firma', e.target.value)} placeholder="Studio sp. z o.o." />
              </Field>
              <Field label={t('settings.nip')} optional>
                <input className="ds-input" value={profile.nip} onChange={e => set('nip', e.target.value)} placeholder="0000000000" />
              </Field>
              <Field label={t('settings.bankAccount')} optional hint={t('dashboardV2.settingsBankAccountHint')}>
                <input className="ds-input" value={profile.konto_bankowe} onChange={e => set('konto_bankowe', e.target.value)} placeholder="PL00 0000 0000 0000 0000 0000 0000" />
              </Field>
              <Field label={t('settings.website')} optional>
                <input className="ds-input" value={profile.strona_www} onChange={e => set('strona_www', e.target.value)} placeholder="www.studio.pl" />
              </Field>

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '4px' }}>
                <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', marginBottom: '8px' }}>
                  {t('dashboardV2.settingsDocumentsTitle')}
                </p>
                {[
                  { field: 'chce_fakture',  label: t('dashboardV2.settingsWantInvoice'),  hint: t('dashboardV2.settingsWantInvoiceHint') },
                  { field: 'chce_rachunek', label: t('dashboardV2.settingsWantReceipt'), hint: t('dashboardV2.settingsWantReceiptHint') },
                ].map(({ field, label, hint }) => (
                  <label key={field} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', userSelect: 'none', padding: '6px 0' }}>
                    <div
                      onClick={() => set(field, !profile[field])}
                      style={{
                        width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                        border: `2px solid ${profile[field] ? 'var(--acc)' : 'var(--border)'}`,
                        background: profile[field] ? 'var(--acc)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s', marginTop: '1px',
                      }}>
                      {profile[field] && (
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                          <path d="M1 3.5l2.5 2.5L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600 }}>{label}</span>
                      <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '1px 0 0' }}>{hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </Section>

          {/* Region */}
          <Section icon={<IconLocation className="w-4 h-4" />} title={t('dashboardV2.settingsSectionRegion')}>
            <div className="form-row-2">
              <Field label={t('settings.country')}>
                <select className="ds-input" value={profile.kraj} onChange={e => set('kraj', e.target.value)}>
                  <option value="PL">PL — Polska</option>
                  <option value="DE">DE — Deutschland</option>
                  <option value="GB">GB — United Kingdom</option>
                  <option value="US">US — United States</option>
                  <option value="FR">FR — France</option>
                  <option value="ES">ES — España</option>
                  <option value="IT">IT — Italia</option>
                  <option value="CZ">CZ — Česko</option>
                  <option value="SK">SK — Slovensko</option>
                  <option value="UA">UA — Україна</option>
                </select>
              </Field>
              <Field label={t('settings.currency')}>
                <select className="ds-input" value={profile.waluta} onChange={e => set('waluta', e.target.value)}>
                  <option value="PLN">PLN — Złoty</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="USD">USD — Dollar</option>
                  <option value="GBP">GBP — Pound</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Panel klienta — adres URL */}
          <Section icon={<IconLink className="w-4 h-4" />} title="Panel klienta">
            <p style={{ fontSize: '10px', color: 'var(--ink3)', lineHeight: 1.5, marginBottom: '12px' }}>
              Pod tym adresem Twoi klienci zobaczą swój podgląd spacerów i faktur.
              Domyślnie to nazwa Twojej firmy. Możesz zmienić — dozwolone tylko litery a-z, cyfry i myślniki.
            </p>
            <Field label="Adres panelu klienta">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', color: 'var(--ink3)', whiteSpace: 'nowrap' }}>
                  my.scanpilots.com /
                </span>
                <input
                  className="ds-input"
                  value={profile.klient_slug}
                  onChange={e => set('klient_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="studio-foto-jan"
                  style={{ flex: 1, minWidth: '160px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
                />
                <Btn
                  variant="primary"
                  disabled={slugSaving || !profile.klient_slug || profile.klient_slug === profileInitial?.klient_slug}
                  onClick={async () => {
                    setSlugSaving(true);
                    try {
                      await api.saveSettings({ ...profileInitial, klient_slug: profile.klient_slug });
                      toast.success('Adres panelu zapisany');
                      // Pobierz spowrotem żeby dostać znormalizowaną wersję od backendu
                      const fresh = await api.getSettings();
                      setProfile(p => ({ ...p, klient_slug: fresh.klient_slug || '' }));
                      setProfileInitial(i => i ? { ...i, klient_slug: fresh.klient_slug || '' } : i);
                    } catch (e) {
                      toast.error(e.message);
                    } finally {
                      setSlugSaving(false);
                    }
                  }}
                >
                  {slugSaving ? 'Zapisuję...' : 'Zapisz'}
                </Btn>
              </div>
              {profile.klient_slug && (
                <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '6px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                  → https://my.scanpilots.com/{profile.klient_slug}
                </p>
              )}
            </Field>
          </Section>

          {/* Save button — zawsze widoczny na dole lewej kolumny */}
          {!isDirty && profileLoaded && (
            <div className="flex items-center gap-2" style={{ fontSize: '11px', color: 'var(--ink3)' }}>
              <IconCheck className="w-3.5 h-3.5" style={{ color: 'var(--ok2)' }} />
              {t('dashboardV2.settingsAllSaved')}
            </div>
          )}
        </div>

        {/* ── PRAWA KOLUMNA: Abonament + Hasło + Preferencje + Konto + Strefa niebezpieczna ─── */}
        <div className="flex flex-col gap-4">

          {/* Plan + status — w trakcie bety wszyscy 'trial' wyświetlani jako BETA, bez countdownu i upsellu PRO. */}
          <Section
            icon={<IconSettings className="w-4 h-4" />}
            title={t('dashboardV2.settingsSectionPlan')}
            accent={profilePlan === 'trial'}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <span className={profilePlan === 'pro' ? 'pill pill-ok' : 'pill pill-warn'} style={{ fontSize: '11px', padding: '4px 10px' }}>
                  {profilePlan === 'pro' ? 'PRO' : 'BETA'}
                </span>
                {profilePlan === 'trial' && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: 'var(--ink2)', fontWeight: 600 }}>
                    Bezpłatnie do 30.06.2026
                  </span>
                )}
              </div>
            </div>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '10px', lineHeight: 1.5 }}>
              {profilePlan === 'trial'
                ? 'Korzystasz z bety ScanPilots — pełen dostęp do wszystkich funkcji bez opłat do końca czerwca 2026. Wszystkie wprowadzone dane (klienci, spacery, faktury) zostaną zachowane jeśli zdecydujesz się korzystać z aplikacji dalej.'
                : t('dashboardV2.kpiPlanProDescription')}
            </p>
          </Section>

          {/* Abonament Matterport */}
          <MatterportSection />

          {/* Hasło */}
          <Section icon={<IconLock className="w-4 h-4" />} title={t('settings.changePassword')}>
            <div className="flex flex-col gap-3">
              <PwField
                label={t('settings.currentPassword')}
                value={pw.stare}
                onChange={e => setPw(p => ({ ...p, stare: e.target.value }))}
                show={showPw.stare}
                onToggle={() => setShowPw(p => ({ ...p, stare: !p.stare }))}
              />
              <div>
                <PwField
                  label={t('settings.newPassword')}
                  value={pw.nowe}
                  onChange={e => setPw(p => ({ ...p, nowe: e.target.value }))}
                  show={showPw.nowe}
                  onToggle={() => setShowPw(p => ({ ...p, nowe: !p.nowe }))}
                />
                {pw.nowe && (
                  <div className="mt-2">
                    <div className="flex gap-1" style={{ marginBottom: '4px' }}>
                      {[1, 2, 3, 4].map(n => (
                        <div key={n} style={{
                          flex: 1, height: '3px', borderRadius: '2px',
                          background: n <= pwStrength.score ? pwStrength.color : 'var(--bg4)',
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: '10px', color: pwStrength.color, fontWeight: 600 }}>
                      {pwStrength.label}
                    </span>
                  </div>
                )}
              </div>
              <PwField
                label={t('settings.confirmNewPassword')}
                value={pw.potwierdz}
                onChange={e => setPw(p => ({ ...p, potwierdz: e.target.value }))}
                show={showPw.potwierdz}
                onToggle={() => setShowPw(p => ({ ...p, potwierdz: !p.potwierdz }))}
                hint={pw.nowe && pw.potwierdz && pw.nowe !== pw.potwierdz ? t('auth.passwordMismatch') : null}
              />
            </div>
            <div className="mt-4">
              <Btn variant="ghost" onClick={savePassword} disabled={pwSaving || !pw.stare || !pw.nowe || pw.nowe !== pw.potwierdz}>
                {pwSaving ? '...' : t('settings.changePassword')}
              </Btn>
            </div>
          </Section>

          {/* Preferencje */}
          <Section icon={<IconSettings className="w-4 h-4" />} title={t('settings.preferences')}>
            <div className="flex flex-col gap-4">
              {/* Język */}
              <div className="flex items-center justify-between">
                <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600 }}>{t('settings.language')}</span>
                <div style={{ display: 'flex', gap: '4px', background: 'var(--bg)', borderRadius: '8px', padding: '3px', border: '1px solid var(--border)' }}>
                  {['pl', 'en'].map(lang => (
                    <button key={lang} onClick={() => toggleLang(lang)}
                      style={{
                        padding: '4px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                        fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em',
                        background: i18n.language === lang ? 'var(--bg4)' : 'transparent',
                        color: i18n.language === lang ? 'var(--ink)' : 'var(--ink3)',
                      }}>
                      {lang === 'pl' ? 'PL' : 'EN'}
                    </button>
                  ))}
                </div>
              </div>
              {/* Motyw */}
              <div className="flex items-center justify-between">
                <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600 }}>{t('settings.theme')}</span>
                <div style={{ display: 'flex', gap: '4px', background: 'var(--bg)', borderRadius: '8px', padding: '3px', border: '1px solid var(--border)' }}>
                  {[
                    { key: 'dark',  Icon: IconMoon, label: t('settings.themeDark')  },
                    { key: 'light', Icon: IconSun,  label: t('settings.themeLight') },
                  ].map(opt => {
                    const Icon = opt.Icon;
                    return (
                      <button key={opt.key} onClick={() => toggleTheme(opt.key)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          padding: '4px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                          fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em',
                          background: theme === opt.key ? 'var(--bg4)' : 'transparent',
                          color: theme === opt.key ? 'var(--ink)' : 'var(--ink3)',
                        }}>
                        <Icon className="w-3 h-3" /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Podpowiedzi */}
              <div className="flex items-center justify-between">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600 }}>{t('settings.hints')}</span>
                  <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('settings.hintsDesc')}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px', background: 'var(--bg)', borderRadius: '8px', padding: '3px', border: '1px solid var(--border)' }}>
                  {[
                    { key: true,  label: t('settings.hintsOn')  },
                    { key: false, label: t('settings.hintsOff') },
                  ].map(opt => (
                    <button key={String(opt.key)} onClick={() => setHintsEnabled(opt.key)}
                      style={{
                        padding: '4px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                        fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em',
                        background: hintsEnabled === opt.key ? 'var(--bg4)' : 'transparent',
                        color: hintsEnabled === opt.key ? 'var(--ink)' : 'var(--ink3)',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* Konto + zmiana emaila */}
          <Section icon={<IconUser className="w-4 h-4" />} title={t('settings.account')}>
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('dashboardV2.settingsEmailLabel')}</span>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 500 }}>{profileEmail}</span>
                  <button
                    onClick={() => setEmailModal(true)}
                    className="hover:underline"
                    style={{ fontSize: '10px', color: 'var(--acc)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                    {t('dashboardV2.settingsChangeAction')}
                  </button>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('settings.memberSince')}</span>
                <span style={{ fontSize: '11px', color: 'var(--ink2)' }}>{memberSince}</span>
              </div>
            </div>

            {/* Niebezpieczna strefa */}
            <div style={{ marginTop: '20px', paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
              <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--err2)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>
                {t('dashboardV2.settingsSectionDanger')}
              </p>
              <button
                onClick={handleLogoutEverywhere}
                className="w-full text-left hover:bg-navy-800/30 transition-colors"
                style={{
                  background: 'none', border: '1px solid var(--border)',
                  borderRadius: '8px', padding: '10px 12px', cursor: 'pointer',
                }}>
                <span style={{ fontSize: '11px', color: 'var(--ink)', fontWeight: 600 }}>{t('dashboardV2.settingsLogoutEverywhere')}</span>
                <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '2px 0 0' }}>
                  {t('dashboardV2.settingsLogoutEverywhereHint')}
                </p>
              </button>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
